import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import {
  YEARS_OF_STUDY, YEAR_OF_STUDY_LABELS, SPECIALISATIONS, SPECIALISATION_SHORT_LABELS,
  AVAILABILITIES, AVAILABILITY_LABELS, ABOUT_ME_MAX,
  type YearOfStudy, type Specialisation, type Availability,
} from "@intern-seek/shared";
import { PageHeading, Alert, Spinner, SkillPicker, TagInput, PillRadioGroup, SegmentedControl } from "../components/ui";

const SPECIALISATION_OPTIONS = SPECIALISATIONS.map((s) => ({ value: s, label: SPECIALISATION_SHORT_LABELS[s] }));
const YEAR_OPTIONS = YEARS_OF_STUDY.map((y) => ({ value: y, label: YEAR_OF_STUDY_LABELS[y] }));
const AVAILABILITY_OPTIONS = AVAILABILITIES.map((a) => ({ value: a, label: AVAILABILITY_LABELS[a] }));

export default function Profile() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["student", "me"],
    queryFn: () => api.get<{ profile: any }>("/students/me"),
  });
  const profile = data?.profile;

  // These need real state to be interactive, unlike the plain inputs below.
  // Synced from the server exactly once -- a ref guard, not a dependency
  // check -- so a later refetch (e.g. after a CV upload) never overwrites a
  // change the student hasn't saved yet.
  const [skills, setSkills] = useState<string[]>([]);
  const [hobbies, setHobbies] = useState<string[]>([]);
  const [achievements, setAchievements] = useState<string[]>([]);
  const [specialisation, setSpecialisation] = useState<Specialisation | "">("");
  const [yearOfStudy, setYearOfStudy] = useState<YearOfStudy | "">("");
  const [availability, setAvailability] = useState<Availability | "">("");
  const [aboutMe, setAboutMe] = useState("");
  const [consent, setConsent] = useState(false);

  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<{ submissionsOpen: boolean }>("/config"),
    staleTime: Infinity,
  });
  const submissionsOpen = config?.submissionsOpen ?? false;
  const loaded = useRef(false);
  useEffect(() => {
    if (profile && !loaded.current) {
      loaded.current = true;
      setSkills((profile.answers?.skills as string[] | undefined) ?? []);
      setHobbies((profile.answers?.hobbies as string[] | undefined) ?? []);
      setAchievements((profile.answers?.achievements as string[] | undefined) ?? []);
      setSpecialisation(profile.specialisation ?? "");
      setYearOfStudy(profile.yearOfStudy ?? "");
      setAvailability((profile.answers?.availability as Availability | undefined) ?? "");
      setAboutMe((profile.answers?.aboutMe as string | undefined) ?? "");
    }
  }, [profile]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["student", "me"] });

  const save = useMutation({
    mutationFn: (body: unknown) => api.put("/students/me", body),
    onSuccess: () => { setMessage({ tone: "success", text: "Saved." }); invalidate(); },
    onError: (err) => setMessage({ tone: "error", text: err instanceof ApiError ? err.message : "Save failed" }),
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.upload("/files/resume", file),
    onSuccess: () => { setMessage({ tone: "success", text: "CV uploaded." }); invalidate(); },
    onError: (err) => setMessage({ tone: "error", text: err instanceof ApiError ? err.message : "Upload failed" }),
  });

  const submit = useMutation({
    mutationFn: () => api.post("/students/me/submit", { consent }),
    onSuccess: () => { setMessage({ tone: "success", text: "Your EOI has been submitted." }); invalidate(); },
    onError: (err) => setMessage({ tone: "error", text: err instanceof ApiError ? err.message : "Submit failed" }),
  });

  useEffect(() => { if (message) window.scrollTo({ top: 0 }); }, [message]);

  if (isLoading) return <Spinner />;

  const onSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    save.mutate({
      firstName: f.get("firstName"), lastName: f.get("lastName"), studentId: f.get("studentId"),
      phone: f.get("phone"), specialisation, yearOfStudy,
      // undefined rather than "" so JSON.stringify drops the key: the schema
      // treats availability as optional, and "" is not a valid enum member.
      answers: {
        ...(profile?.answers as Record<string, unknown> | undefined),
        skills, hobbies, achievements,
        availability: availability || undefined,
        aboutMe: aboutMe.trim() || undefined,
      },
    });
  };

  return (
    <>
      <PageHeading title="Expression of interest" description="Your details and CV, shared with employers you apply to." />
      {message && <Alert tone={message.tone}>{message.text}</Alert>}

      <form onSubmit={onSave} className="card mb-6 grid gap-4 sm:grid-cols-2">
        <F name="firstName" label="First name" defaultValue={profile?.firstName} required />
        <F name="lastName" label="Last name" defaultValue={profile?.lastName} required />
        <F name="studentId" label="Student ID" defaultValue={profile?.studentId} required inputMode="numeric" />
        <F name="phone" label="Phone" defaultValue={profile?.phone} type="tel" />
        <div>
          <span className="label">
            Specialisation <span aria-hidden className="text-red-700">*</span>
          </span>
          <PillRadioGroup options={SPECIALISATION_OPTIONS} value={specialisation} onChange={setSpecialisation} ariaLabel="Specialisation" />
        </div>
        <div>
          <span className="label">
            Year of study <span aria-hidden className="text-red-700">*</span>
          </span>
          <SegmentedControl options={YEAR_OPTIONS} value={yearOfStudy} onChange={setYearOfStudy} ariaLabel="Year of study" />
        </div>
        <div className="sm:col-span-2">
          <span className="label">Availability</span>
          <PillRadioGroup options={AVAILABILITY_OPTIONS} value={availability} onChange={setAvailability} ariaLabel="Availability" />
        </div>

        <div className="sm:col-span-2">
          <label className="label" htmlFor="aboutMe">About me</label>
          <p className="mb-2 text-sm text-uoa-grey">
            Your own pitch, and the main thing employers read. Roughly one page.
          </p>
          <textarea
            id="aboutMe"
            rows={10}
            maxLength={ABOUT_ME_MAX}
            className="input"
            value={aboutMe}
            onChange={(e) => setAboutMe(e.target.value)}
            placeholder="What you are interested in, what you have built, what you want from an internship."
          />
          <p className={`mt-1 text-right text-xs ${aboutMe.length >= ABOUT_ME_MAX ? "text-red-700" : "text-uoa-grey"}`}>
            {aboutMe.length} / {ABOUT_ME_MAX}
          </p>
        </div>

        <div className="sm:col-span-2">
          <span className="label">Skills</span>
          <p className="mb-2 text-sm text-uoa-grey">Pick as many as apply. You can change these at any time.</p>
          <SkillPicker value={skills} onChange={setSkills} />
        </div>

        <div>
          <span className="label">Achievements</span>
          <p className="mb-2 text-sm text-uoa-grey">Type a comma to add each one.</p>
          <TagInput
            value={achievements}
            onChange={setAchievements}
            ariaLabel="Achievements"
            placeholder="e.g. class rep, AWS certification"
            maxLength={60}
          />
        </div>

        <div>
          <span className="label">Hobbies</span>
          <p className="mb-2 text-sm text-uoa-grey">Type a comma to add each one.</p>
          <TagInput value={hobbies} onChange={setHobbies} ariaLabel="Hobbies" placeholder="e.g. chess, hiking" />
        </div>

        {/* TODO: remaining EOI fields, pending the client's field list. */}
        <div className="sm:col-span-2">
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "Saving..." : "Save details"}
          </button>
        </div>
      </form>

      <div className="card mb-6">
        <h2 className="mb-1 text-lg font-semibold text-heading">
          Your CV <span className="font-normal text-uoa-grey">(optional)</span>
        </h2>
        <p className="mb-4 text-sm text-uoa-grey">
          An attachment to your About me section, not a replacement for it. PDF only,
          maximum 5 MB. Uploading a new CV replaces the previous one.
        </p>

        {profile?.resumeFile ? (
          <p className="mb-4 text-sm">
            Current: <a className="text-uoa-blue underline dark:text-uoa-blue-light" href={`/api/files/${profile.resumeFile.id}`} target="_blank" rel="noreferrer">
              {profile.resumeFile.filename}
            </a>{" "}
            <span className="text-uoa-grey">({Math.round(profile.resumeFile.sizeBytes / 1024)} KB)</span>
          </p>
        ) : (
          <p className="mb-4 text-sm text-uoa-grey">No CV uploaded yet.</p>
        )}

        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          aria-label="Choose a PDF CV"
          className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-uoa-blue file:px-4 file:py-2 file:text-white"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            if (fileInput.current) fileInput.current.value = "";
          }}
          disabled={!profile || upload.isPending}
        />
        {!profile && <p className="field-error">Save your details before uploading a CV.</p>}
      </div>

      <div className="card">
        <h2 className="mb-1 text-lg font-semibold text-heading">Submit</h2>
        <p className="mb-4 text-sm text-uoa-grey">
          {profile?.submittedAt
            ? `Submitted on ${new Date(profile.submittedAt).toLocaleDateString("en-NZ")}. You can still update your details.`
            : "Submitting makes you visible to employers and lets you apply for positions."}
        </p>
        {!submissionsOpen && (
          <p className="mb-4 rounded-md border border-line bg-uoa-sand px-3 py-2 text-sm">
            Submissions are not open yet. Save your details now and they will be ready to submit when they open.
          </p>
        )}
        <label className="mb-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            By submitting my details I understand that they will be shared with industry partners registered
            with Intern-Seek.
          </span>
        </label>
        <button
          className="btn-primary"
          onClick={() => submit.mutate()}
          disabled={!submissionsOpen || !consent || submit.isPending}
        >
          {profile?.submittedAt ? "Re-submit" : "Submit expression of interest"}
        </button>
      </div>
    </>
  );
}

function F({ label, name, className = "", ...rest }: { label: string; name: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={className}>
      <label className="label" htmlFor={name}>
        {label} {rest.required && <span aria-hidden className="text-red-700">*</span>}
      </label>
      <input id={name} name={name} className="input" {...rest} />
    </div>
  );
}
